import { ThoughtspotIntegrationService } from '@mode/shared/util-thoughtspot-integrated';
import { merge, Observable } from 'rxjs';

import { filter, map, withLatestFrom } from 'rxjs/operators';
import { Actions } from '~/actions';
import { ofType } from '@mode/legacy/util-domain';

import { AppStore } from '~/types/main.types';
import { reportEditActions } from '@mode/report-editor/domain';
import { RUNNING_QUERY_STATES } from '@mode/shared/contract-common';
import { updateFavIcon } from '@mode/shared/util-js';

import { SelectedDefinitionActionType } from '@mode/legacy/util-domain';
import { QueryActionType } from '../actions/query.actions';
import { ReportRunActionType } from '../actions/report-run.actions';
import { SelectedReportSelectors } from '../selectors/selected-report.selectors';

export class FavIconService {
  static $inject = ['RxStoreService', 'actions$', 'ThoughtspotIntegrationService'];
  readonly isAnalystStudio = this.thoughtspotIntegrationService.currOrgIntegrated();

  constructor(
    store: AppStore,
    actions$: Observable<Actions>,
    private thoughtspotIntegrationService: ThoughtspotIntegrationService
  ) {
    const RUNNING_FAV_STATE = 'running';
    const FAILED_FAV_STATE = 'failed';
    const CANCELLED_STATE = 'cancelled';
    const WARNING_STATE = 'warning';
    const SUCCEEDED_FAV_STATE = 'succeeded';
    const DEFAULT_FAV_STATE = 'default';

    const DEFAULT_QUERY_STATE = { state: DEFAULT_FAV_STATE };

    const newQuerySelection$ = actions$.pipe(
      ofType(QueryActionType.CreateQuery),
      map(() => DEFAULT_FAV_STATE)
    );

    const existingQuerySelection$ = actions$.pipe(
      ofType(reportEditActions.selectQuery),
      withLatestFrom(store.select(SelectedReportSelectors.selectedQueryRunRecord)),
      map(([, { state } = DEFAULT_QUERY_STATE]) => (RUNNING_QUERY_STATES.includes(state) ? RUNNING_FAV_STATE : state))
    );

    const queryRunning$ = merge(
      store // This stream checks if anything is running. if so, keep favicon as running
        .select(SelectedReportSelectors.getQueryRuns)
        .pipe(filter((queryRuns) => queryRuns.getRecords().some(({ state }) => RUNNING_QUERY_STATES.includes(state)))),
      actions$.pipe(ofType(ReportRunActionType.ExecuteReportRun))
    ).pipe(map(() => RUNNING_FAV_STATE));

    // definition specific to failed || succeeded
    const definitionFailed$ = actions$.pipe(
      ofType(SelectedDefinitionActionType.LoadDefinitionQueryRunSuccess),
      filter(({ resource: { state } }) => [FAILED_FAV_STATE, CANCELLED_STATE, WARNING_STATE].includes(state)),
      map(() => FAILED_FAV_STATE)
    );

    const definitionSucceeded$ = actions$.pipe(
      ofType(SelectedDefinitionActionType.LoadDefinitionQueryRunSuccess),
      filter((action) => action.resource.state === SUCCEEDED_FAV_STATE),
      map(() => SUCCEEDED_FAV_STATE)
    );

    const definitionRunning$ = actions$.pipe(
      ofType(SelectedDefinitionActionType.LoadDefinitionQueryRunSuccess),
      filter((action) => RUNNING_QUERY_STATES.includes(action.resource.state)),
      map(() => RUNNING_FAV_STATE)
    );

    // report failed || succeeded stream
    const reportFailOrSuccess$ = actions$.pipe(
      ofType(ReportRunActionType.PollReportRunComplete),
      withLatestFrom(store.select(SelectedReportSelectors.selectedQueryRunRecord)),
      map(([, queryRun]) => {
        if (queryRun !== undefined) {
          return queryRun.state;
        } else {
          return DEFAULT_FAV_STATE;
        }
      })
    );

    // all the observables that can affect favicon state
    merge(
      queryRunning$,
      reportFailOrSuccess$,
      definitionFailed$,
      definitionSucceeded$,
      definitionRunning$,
      existingQuerySelection$,
      newQuerySelection$
    ).subscribe((state) => updateFavIcon(state, this.isAnalystStudio));
  }
}
